import { useState } from 'react'
import { Ban, ChevronDown, ChevronUp, Receipt } from 'lucide-react'
import type { Category, Order } from '@/types'

/**
 * Statuses a placed line can be voided from. Retail lines are `pending` until
 * paid (then the order completes) or voided; only `pending` is voidable.
 *
 * Mirrors voidableItemStatus() in backend/internal/handlers/order_items.go —
 * the backend refuses anything this list wrongly offers, so the two must agree.
 * Too NARROW here silently kills the feature with no error anywhere
 * (audit/SERVED_ITEM_VOID_GATE_2026-08-07.md).
 */
export const VOIDABLE_STATUSES = new Set(['pending'])

export interface SentItemsSectionProps {
  order: Order | null
  categoryById: Map<string, Category>
  /** Category accent color function (usually inherited from the parent). */
  categoryColor: (cat: Category | undefined, fallback: string) => string
  formatCurrency: (n: number) => string
  /** Default expanded state for the section. */
  defaultExpanded?: boolean
  /** When provided, a void button is shown on each voidable row. */
  onRequestVoid?: (itemId: string, itemName: string, qty: number, unitPrice: number) => void
}

export function SentItemsSection({
  order,
  categoryById,
  categoryColor,
  formatCurrency,
  defaultExpanded = true,
  onRequestVoid,
}: SentItemsSectionProps) {
  const items = (order?.items ?? []).filter((i) => i.status !== 'voided')
  const [expanded, setExpanded] = useState(defaultExpanded)

  if (!order || items.length === 0) return null

  const total = items.reduce((s, i) => s + i.total_price, 0)

  return (
    <section aria-labelledby="sent-items-heading" className="rounded-lg border border-border bg-muted/15">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 rounded-t-lg px-3 py-2.5 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            On sale
          </div>
          <div id="sent-items-heading" className="flex items-center gap-2 text-sm font-medium">
            <Receipt className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" aria-hidden />
            Order #{order.order_number} · {items.length}
            <span className="text-muted-foreground font-normal">items</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold tabular-nums">{formatCurrency(total)}</span>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
        </div>
      </button>
      {expanded && (
        <div className="overflow-x-auto px-2 pb-3">
          <table className="w-full min-w-[268px] table-fixed border-collapse border border-border text-sm">
            <colgroup>
              <col style={{ width: '46%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '18%' }} />
            </colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/60">
                <th className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Item
                </th>
                <th className="px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Qty
                </th>
                <th className="px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Amount
                </th>
                <th className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {onRequestVoid ? '' : 'Status'}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const exCat = item.product?.category_id ? categoryById.get(item.product.category_id) : undefined
                const exAccent = categoryColor(exCat, item.product?.name ?? 'Item')
                const lineDiscount = item.discount_amount ?? 0
                return (
                  <tr
                    key={item.id}
                    className="border-b border-border last:border-b-0 border-l-[3px]"
                    style={{
                      borderLeftColor: exAccent,
                      backgroundColor: `color-mix(in srgb, ${exAccent} 10%, var(--card))`,
                    }}
                  >
                    <td className="min-w-0 px-2 py-2">
                      <span className="line-clamp-2 font-medium leading-snug">
                        {item.product?.name ?? 'Item'}
                      </span>
                      {lineDiscount > 0 && (
                        <div className="mt-0.5 text-[11px] tabular-nums text-amber-700 dark:text-amber-300">
                          Discount −{formatCurrency(lineDiscount)}
                          {item.discount_percent != null ? ` (${item.discount_percent}%)` : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-sm tabular-nums">{item.quantity}</td>
                    <td className="px-2 py-2 text-right text-sm font-medium tabular-nums">
                      {formatCurrency(item.total_price)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {onRequestVoid && VOIDABLE_STATUSES.has(item.status) ? (
                        <button
                          type="button"
                          title="Void item"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRequestVoid(item.id, item.product?.name ?? 'Item', item.quantity, item.unit_price)
                          }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Ban className="h-3.5 w-3.5" aria-hidden />
                          <span className="sr-only">Void {item.product?.name ?? 'item'}</span>
                        </button>
                      ) : (
                        <span className="text-[11px] text-muted-foreground capitalize">{item.status}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
